import React, { useEffect, useState } from 'react';
import { 
  Home, 
  TrendingUp, 
  Users, 
  FileText, 
  DollarSign,
  AlertCircle 
} from 'lucide-react';
import api from '../lib/api';
import { format, parseISO, isBefore, addDays, isAfter } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Stats {
  properties: number;
  activeLeases: number;
  pendingCharges: number;
  monthlyRevenue: number;
}

interface Charge {
  id: string;
  competence: string;
  dueDate: string;
  amount: number;
  paidAmount?: number;
  status: string;
  paymentDate?: string;
  lease?: {
    code: string;
    property?: {
      title?: string;
      code: string;
      address?: string;
    };
  };
}

interface Lease {
  id: string;
  code: string;
  endDate?: string;
  status: string;
  property?: {
    title?: string;
    code: string;
  };
  tenant?: {
    name: string;
  };
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats>({
    properties: 0,
    activeLeases: 0,
    pendingCharges: 0,
    monthlyRevenue: 0,
  });
  const [recentCharges, setRecentCharges] = useState<Charge[]>([]);
  const [expiringLeases, setExpiringLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [propertiesRes, leasesRes, chargesRes] = await Promise.all([
        api.get('/properties?limit=2000'),
        api.get('/leases?limit=2000'),
        api.get('/charges?limit=5000'),
      ]);

      // Extrair arrays das respostas paginadas
      const propertiesData = propertiesRes.data.data || [];
      const leasesData = leasesRes.data.data || [];
      const chargesData = chargesRes.data.data || [];

      // Totais reais (usa o meta.total quando disponível, senão o tamanho da lista)
      const totalProperties = propertiesRes.data.meta?.total ?? propertiesData.length;

      // Calcular estatísticas
      const activeLeasesList = leasesData.filter((l: any) => l.status === 'ACTIVE' || l.isActive);
      const pendingChargesList = chargesData.filter((c: any) =>
        c.status === 'PENDING' || c.status === 'OVERDUE'
      );
      // Receita recebida no mês corrente (pelos pagamentos das cobranças)
      const now = new Date();
      const paidCharges = chargesData.filter((c: any) => c.status === 'PAID');
      const monthRevenue = paidCharges.reduce((sum: number, c: any) => {
        const ref = c.paymentDate || c.dueDate;
        if (!ref) return sum;
        const d = new Date(ref);
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return sum;
        return sum + (parseFloat(c.paidAmount ?? c.amount) || 0);
      }, 0);

      setStats({
        properties: totalProperties,
        activeLeases: activeLeasesList.length,
        pendingCharges: pendingChargesList.length,
        monthlyRevenue: monthRevenue,
      });

      // Cobranças recentes (últimas 5)
      const sortedCharges = [...chargesData]
        .sort((a, b) => new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime())
        .slice(0, 5);
      setRecentCharges(sortedCharges);

      // Contratos vencendo nos próximos 60 dias
      const today = new Date();
      const in60Days = addDays(today, 60);
      const expiringList = leasesData
        .filter((l: Lease) => {
          if (!l.endDate || l.status !== 'ACTIVE') return false;
          const endDate = parseISO(l.endDate);
          return isAfter(endDate, today) && isBefore(endDate, in60Days);
        })
        .sort((a: Lease, b: Lease) => 
          new Date(a.endDate!).getTime() - new Date(b.endDate!).getTime()
        )
        .slice(0, 5);
      setExpiringLeases(expiringList);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Imóveis',
      value: stats.properties,
      icon: Home,
      color: 'bg-blue-500',
      trend: `${stats.properties} total`,
    },
    {
      title: 'Contratos Ativos',
      value: stats.activeLeases,
      icon: FileText,
      color: 'bg-green-500',
      trend: `${stats.activeLeases} ativos`,
    },
    {
      title: 'Cobranças Pendentes',
      value: stats.pendingCharges,
      icon: AlertCircle,
      color: 'bg-yellow-500',
      trend: `${stats.pendingCharges} pendentes`,
    },
    {
      title: 'Receita Recebida (mês)',
      value: `R$ ${stats.monthlyRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
      icon: DollarSign,
      color: 'bg-purple-500',
      trend: 'Recebido no mês',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'Pago';
      case 'PENDING':
        return 'Pendente';
      case 'OVERDUE':
        return 'Vencido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getDaysUntilExpiry = (endDate: string) => {
    const today = new Date();
    const expiry = parseISO(endDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <button 
          className="btn-primary"
          onClick={() => window.location.href = '/leases'}
        >
          Novo Contrato
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.title} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  <p className="text-sm text-gray-500 mt-2">
                    {stat.trend}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cobranças Recentes e Contratos Vencendo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cobranças Recentes */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Cobranças Recentes</h2>
          <div className="space-y-3">
            {recentCharges.length > 0 ? (
              recentCharges.map((charge) => (
                <div key={charge.id} className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium">
                      {charge.lease?.property?.title || charge.lease?.property?.code || 'Imóvel'}
                    </p>
                    <p className="text-sm text-gray-600">
                      Venc: {format(parseISO(charge.dueDate), 'dd/MM/yyyy', { locale: ptBR })} - 
                      R$ {(parseFloat(charge.amount as any) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(charge.status)}`}>
                    {getStatusLabel(charge.status)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-sm">Nenhuma cobrança encontrada</p>
            )}
          </div>
        </div>

        {/* Contratos Vencendo */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Contratos Vencendo</h2>
          <div className="space-y-3">
            {expiringLeases.length > 0 ? (
              expiringLeases.map((lease) => {
                const days = getDaysUntilExpiry(lease.endDate!);
                return (
                  <div key={lease.id} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <p className="font-medium">Contrato {lease.code}</p>
                      <p className="text-sm text-gray-600">
                        {lease.tenant?.name} - Vence em {days} dias
                      </p>
                    </div>
                    <button 
                      className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      onClick={() => window.location.href = `/leases`}
                    >
                      Ver
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm">Nenhum contrato vencendo nos próximos 60 dias</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
